import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MessageSquare, Swords, Users, Lightbulb } from 'lucide-react';
import Header from '../components/layout/Header';
import { Footer } from '../components/marketing';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      {/* Hero — Brand Manifesto */}
      <section className="pt-24 pb-16 px-4 md:pt-36 md:pb-24 md:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[rgba(245,241,232,0.08)] text-[rgba(245,241,232,0.48)] text-xs font-mono mb-8 tracking-wider">
              <Swords className="w-3 h-3 text-[#FF4D00]" />
              MANIFESTO
            </div>

            <h1 className="text-display-lg md:text-display-xl font-display text-[#F5F1E8] uppercase mb-8 leading-[0.95]">
              Every Decision<br />
              Deserves a<br />
              <span className="text-[#FF4D00]">Dissenting Voice</span>
            </h1>

            <div className="space-y-6 text-lg text-[rgba(245,241,232,0.72)] font-body leading-relaxed max-w-2xl">
              <p>
                The most dangerous decisions are the ones where everyone agrees too quickly.
                Groupthink kills companies, ruins investments, and wastes years of research.
              </p>
              <p>
                We built Fray because we believe every important decision deserves structured
                adversarial analysis — not polite consensus from a single AI voice, but genuine
                friction from multiple perspectives that challenge assumptions and surface blind spots.
              </p>
              <p className="text-[#F5F1E8] font-medium">
                Fray is decision insurance. AI that argues before your team commits.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Dissent Matters */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xs font-mono text-[#FF4D00]">01</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">Why Dissent Matters</h2>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: MessageSquare,
                title: 'Single-Voice AI is Dangerous',
                body: 'When you ask one AI model for advice, you get one perspective. It agrees with your framing, validates your assumptions, and tells you what you want to hear. That\'s not analysis — it\'s an echo chamber.',
              },
              {
                icon: Swords,
                title: 'Friction Reveals Truth',
                body: 'The best decisions emerge when ideas compete. A Devil\'s Advocate, a Risk Analyst, and a Pragmatist don\'t just agree — they fight. The disagreements are where the real insights live.',
              },
              {
                icon: Lightbulb,
                title: 'Consensus Must Be Earned',
                body: 'Fray measures genuine consensus through rounds of structured debate. When five AI voices converge on an answer after genuine disagreement, you can trust the analysis. That\'s decision insurance.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)]"
              >
                <item.icon className="w-5 h-5 text-[#FF4D00] mb-4" />
                <h3 className="text-sm font-display font-bold text-[#F5F1E8] uppercase tracking-tight mb-3">{item.title}</h3>
                <p className="text-sm text-[rgba(245,241,232,0.64)] leading-relaxed font-body">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xs font-mono text-[#FF4D00]">02</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">The Team</h2>
            </div>
            <div className="grid md:grid-cols-[200px_1fr] gap-8 items-start">
              <div className="bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] aspect-square flex items-center justify-center">
                <Users className="w-12 h-12 text-[rgba(245,241,232,0.16)]" />
              </div>
              <div>
                <h3 className="text-lg font-display font-bold text-[#F5F1E8] uppercase mb-1">Jim Perkins</h3>
                <p className="text-xs font-mono text-[#FF4D00] mb-4 tracking-wider">FOUNDER</p>
                <div className="space-y-4 text-sm text-[rgba(245,241,232,0.72)] leading-relaxed font-body">
                  <p>
                    Fray started from a simple observation: the most expensive business mistakes come
                    from decisions where nobody pushed back. Advisory boards are expensive. Consultants
                    tell you what you want to hear. And single-model AI chatbots are the most agreeable
                    entities on the planet.
                  </p>
                  <p>
                    What if you could assemble a committee of AI voices — each with different perspectives,
                    different risk tolerances, different expertise — and force them to genuinely disagree
                    before reaching consensus? That's Fray.
                  </p>
                  <p>
                    We're a two-person team with AI doing the work of twenty. Our burn rate is under
                    $2,000/month. We don't need venture funding to build something extraordinary — we
                    need customers who value rigorous decision-making.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xs font-mono text-[#FF4D00]">03</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">Vision</h2>
            </div>
            <blockquote className="border-l-2 border-[#FF4D00] pl-6 py-2">
              <p className="text-xl text-[#F5F1E8] font-body leading-relaxed italic">
                "We're building toward a world where no important decision is made without structured
                adversarial analysis. Where 'I asked five AI experts and they all fought about it'
                becomes as standard as 'I ran the numbers.'"
              </p>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-display-md font-display text-[#F5F1E8] uppercase mb-4">
              Join the Debate
            </h2>
            <p className="text-sm text-[rgba(245,241,232,0.72)] mb-8 font-body">
              Run your first Fray for free. See what happens when AI voices genuinely disagree about your decision.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
            >
              Start Debating Free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
